import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Clock, RefreshCw, XCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import { damageNoteService } from '../../services/damageNoteService';
import { DamageNoteListDTO, DamageNoteStatus } from '../../types';
import './DamageNotePage.css';

// Statuses that represent active workflow (submitted but not yet terminal)
const WORKFLOW_STATUSES: DamageNoteStatus[] = ['PendingApproval', 'Approved', 'Rejected'];

const STATUS_CLASS: Record<DamageNoteStatus, string> = {
  Draft:           'ims-badge dn-badge-draft',
  PendingApproval: 'ims-badge dn-badge-pending',
  Approved:        'ims-badge dn-badge-approved',
  Rejected:        'ims-badge dn-badge-rejected',
  Posted:          'ims-badge dn-badge-posted',
  Cancelled:       'ims-badge dn-badge-cancelled',
};

const STATUS_LABEL: Record<DamageNoteStatus, string> = {
  Draft:           'Draft',
  PendingApproval: 'Pending Approval',
  Approved:        'Approved',
  Rejected:        'Rejected',
  Posted:          'Posted',
  Cancelled:       'Cancelled',
};

const DamageNoteApprovalsPage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const [items,   setItems]   = useState<DamageNoteListDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res     = await damageNoteService.getAll(1, 500);
      const payload = res.data?.data;
      const all: DamageNoteListDTO[] = (payload as any)?.data ?? (payload as any) ?? [];
      setItems(all.filter(d => WORKFLOW_STATUSES.includes(d.status)));
    } catch {
      setError(t('damageNote.failedToLoad', 'Failed to load damage notes.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const pendingCount  = items.filter(d => d.status === 'PendingApproval').length;
  const approvedCount = items.filter(d => d.status === 'Approved').length;
  const rejectedCount = items.filter(d => d.status === 'Rejected').length;

  return (
    <Layout>
      <div className="ims-page dn-page">

        {/* Header */}
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">
              {t('damageNote.approvals', 'Damage Note Approvals')}
            </h1>
            <p style={{ color: 'var(--ims-text-sub)', fontSize: 13, marginTop: 2 }}>
              {t('damageNote.approvalsDesc', 'Review and action damage notes awaiting approval')}
            </p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={fetchAll} disabled={loading}>
              <RefreshCw
                size={14}
                strokeWidth={2}
                className={loading ? 'refresh-icon-spinning' : ''}
              />
              {t('common.refresh')}
            </button>
            <button
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate('/damage-notes')}
            >
              {t('damageNote.backToList', 'Back to List')}
            </button>
          </div>
        </div>

        {/* Stat cards */}
        {!loading && (
          <div className="ims-stats-row" style={{ marginBottom: 16 }}>
            <div className="ims-stat-card">
              <div className="ims-stat-icon dn-stat-icon-total">
                <AlertTriangle size={22} strokeWidth={1.75} />
              </div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{items.length}</span>
                <span className="ims-stat-label">
                  {t('damageNote.inWorkflow', 'In Workflow')}
                </span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon dn-stat-icon-pending">
                <Clock size={22} strokeWidth={1.75} />
              </div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{pendingCount}</span>
                <span className="ims-stat-label">
                  {t('damageNote.pendingApproval', 'Pending Approval')}
                </span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon dn-stat-icon-posted">
                <CheckCircle2 size={22} strokeWidth={1.75} />
              </div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{approvedCount}</span>
                <span className="ims-stat-label">
                  {t('damageNote.approved', 'Approved')}
                </span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon dn-stat-icon-loss">
                <XCircle size={22} strokeWidth={1.75} />
              </div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{rejectedCount}</span>
                <span className="ims-stat-label">
                  {t('damageNote.rejected', 'Rejected')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Loading spinner */}
        {loading && (
          <div className="ims-loading" style={{ padding: '48px 0' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && items.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '64px 24px',
            color: 'var(--ims-text-muted)', background: '#fff',
            borderRadius: 12, border: '1px solid var(--ims-border)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ims-text-main)', marginBottom: 6 }}>
              {t('damageNote.noPendingApprovals', 'No damage notes pending approval')}
            </div>
            <div style={{ fontSize: 13 }}>
              {t('damageNote.noPendingApprovalsDesc', 'All damage notes are in draft, posted, or cancelled state.')}
            </div>
          </div>
        )}

        {/* Approval cards */}
        {!loading && items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {items.map(item => (
              <div key={item.id} className="ims-card" style={{ margin: 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: 12, padding: '16px 20px',
                }}>
                  {/* Left: damage note info */}
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                        {item.damageNoteNumber}
                      </span>
                      <span className={STATUS_CLASS[item.status] ?? 'ims-badge'}>
                        {STATUS_LABEL[item.status] ?? item.status}
                      </span>
                    </div>
                    <div style={{
                      fontSize: 13, color: 'var(--ims-text-sub)',
                      display: 'flex', gap: 16, flexWrap: 'wrap',
                    }}>
                      {item.warehouseName && (
                        <span>
                          <strong>{t('damageNote.warehouse', 'Warehouse')}:</strong> {item.warehouseName}
                        </span>
                      )}
                      <span>
                        <strong>{t('damageNote.damageDate', 'Date')}:</strong>{' '}
                        {new Date(item.damageDate).toLocaleDateString()}
                      </span>
                      <span>
                        <strong>{t('damageNote.items', 'Items')}:</strong> {item.itemCount}
                      </span>
                      <span>
                        <strong>{t('damageNote.estimatedValue', 'Est. Value')}:</strong>{' '}
                        {item.totalEstimatedValue.toFixed(2)}
                      </span>
                      {item.reportedByName && (
                        <span>
                          <strong>{t('damageNote.reportedBy', 'Reported By')}:</strong> {item.reportedByName}
                        </span>
                      )}
                    </div>

                    {/* Contextual status hint */}
                    {item.status === 'PendingApproval' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#92400e', fontWeight: 500 }}>
                        {t('damageNote.awaitingApproval', 'Awaiting approval — open to approve or reject')}
                      </div>
                    )}
                    {item.status === 'Approved' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#15803d', fontWeight: 500 }}>
                        {t('damageNote.approvedReadyToPost', 'Approved — ready to post to inventory')}
                      </div>
                    )}
                    {item.status === 'Rejected' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b', fontWeight: 500 }}>
                        {t('damageNote.rejectedResubmit', 'Rejected — reporter must resubmit')}
                      </div>
                    )}
                  </div>

                  {/* Right: action button */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <button
                      className="ims-btn ims-btn-secondary"
                      style={{ fontSize: 13 }}
                      onClick={() => navigate(`/damage-notes/${item.id}`)}
                    >
                      {item.status === 'Approved'
                        ? t('damageNote.postToInventory', 'Post to Inventory')
                        : t('common.view')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DamageNoteApprovalsPage;
